/****************************************************
 * Module Name:  stock.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
// React & Hooks
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Utility & Constants
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import WatchlistProxy from "../../utility/proxy/manager/watchlistProxy";
import LabTestProxy from "../../utility/proxy/manager/labTestProxy";

// Components - UI & Modals
import StockSearchCriteria from "./stockSearchCriteria";
import CreateMemoModal from "./createMemoModal";
import SellMemoModal from "./sellMemoModal";
import HoldMemoModal from "./holdStoneModal";
import PacketHistoryModal from "../common/packetHistoryModal";
import StoneDetailModal from "../common/stoneDetailModal";
import OfferModal from "./OfferModal";
import SendMessageModal from "../common/sendMessageModal";
import ReleaseStoneModal from "./releaseStoneModal";
import CriteriaLinkModal from "./criteriaLinkModal";
import RelabModal from "./relabModal";
import StoneMediaModal from "../common/stoneMediaModal";
import MixStoneBucketModal from "./mixStoneBucketModal";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";

// Material UI
import { Box, Button, Tooltip, Drawer, Badge, Switch, FormControlLabel, Typography, Menu, MenuItem, Stack, } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { LoadingButton } from "@mui/lab";
import { styled, alpha } from "@mui/material/styles";

// MUI Icons
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import TuneIcon from "@mui/icons-material/Tune";
import VideoLibraryTwoToneIcon from "@mui/icons-material/VideoLibraryTwoTone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Download from "@mui/icons-material/Download";
import BiotechIcon from "@mui/icons-material/Biotech";
import ReplyIcon from "@mui/icons-material/Reply";
import { MdUnpublished } from "react-icons/md";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { FaBoxOpen } from "react-icons/fa";
import { BsBucketFill } from "react-icons/bs";
import { TbTableExport } from "react-icons/tb";

// DataGrid
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import renderCellExpand from "../common/dataGridCellExpand";

// Assets
import colourchart from "../../images/ColorScheme.png";

// Excel Export Function
import { createExcelFile } from "../common/createExcelFile";
import WatchlistModal from "../watchlist/watchlistModal";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  "& .super-app-theme--AVAILABLE": {
    color: "#000",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    "&:hover": {
      color: "#000",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
  "& .super-app-theme--MEMO_OUT": {
    backgroundColor: "#b6b9d6",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    color: "#000",
    "&:hover": {
      backgroundColor: "#b6b9d6",
      color: "#000",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
  "& .super-app-theme--HOLD": {
    backgroundColor: "#b6b9d6",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    color: "#000",
    "&:hover": {
      backgroundColor: "#b6b9d6",
      color: "#000",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
  '& .MuiDataGrid-columnHeader': {
    color: '#fff',
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    backgroundColor: 'grey',
  },
  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
    py: "8px",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
  },
  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
    py: "15px",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
  },
  "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
    py: "22px",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
  },

}));

const NewStoneSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const StyledMenuButtons = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 100,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      fontSize: 15,
      padding: "4px 12px",
      '& .MuiSvgIcon-root': {
        fontSize: 16,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

export default function stock() {
  const [stockCriteriaState, setStockCriteriaState] = useState('');
  const [stockData, setStockData] = useState([]);
  const [searchCriteria, setSearchCriteria] = useState("");
  const [stockLoader, setStockLoader] = useState(false);
  const [show, setShow] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingProformaButton, setLoadingProformaButton] = useState(false);
  const [warning, setWarning] = useState(false);
  const [warningmessage, setWarningMessage] = useState(false);
  const [stoneId, setStoneId] = useState("");
  const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerId, setCustomerId] = useState(Constants.loggedInUser.usertype === 'C' ? { label: Constants.loggedInUser.companyname, value: Constants.loggedInUser.id } : "");
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("");
  const [showSellModal, setShowSellModal] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdData, setHoldData] = useState({});
  const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
  const [packetId, setPacketId] = useState("");
  const [kapanId, setKapanId] = useState("");
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
  const [selectedTotalSize, setSelectedTotalSize] = useState("0");
  const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
  const [selectedavgPrice, setSelectedavgPrice] = useState("0");
  const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [sortModel, setSortModel] = React.useState([]);
  const [columnNames, setColumnNames] = React.useState([]);
  const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);

  const [showOfferModal, setShowOfferModal] = useState(false);
  const [messageModal, setMessageModal] = React.useState(false);
  const [message, setMessage] = useState("");
  const [rowData, setRowData] = React.useState({});
  const [stoneDataMap, setStoneDataMap] = useState({});

  const [filters, setFilters] = useState("");
  const [showReleaseModal, setShowReleaseModal] = useState(false);

  const [uploadLoader, setUploadLoader] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [showCriteriaLinkModal, setShowCriteriaLinkModal] = useState("");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const guid = searchParams.get('id');

  const [flag, setFlag] = useState(guid ? true : false);

  const [showRelabModal, setShowRelabModal] = useState(false);
  const [showNewStone, setShowNewStone] = useState(false);
  const [newStoneList, setNewStoneList] = useState([]);
  const [allStoneList, setAllStoneList] = useState([]);

  const [stoneMediaURL, setStoneMediaURL] = useState([]);
  const [showStoneMediaModal, setShowStoneMediaModal] = useState(false);

  const [watchlistLoader, setWatchlistLoader] = useState(false);
  const [offlineLoader, setOfflineLoader] = useState(false);
  const [disableAllButtons, setDisableAllButtons] = useState(false);

  const [showMixBucketModal, setShowMixBucketModal] = useState(false);
  const [bucketData, setBucketData] = useState([]);
  const [paginationModel, setPaginationModel] = useState(Constants.pageSize);

  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [watchlistData, setWatchlistData] = useState([]);
  const [activeTabValue, setActiveTabValue] = useState("");

  // eslint-disable-next-line
  const [local_Permission, setLocal_Permission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.OPERATION_STOCK
    )
  );
  // eslint-disable-next-line
  const [sellPermission, setSellPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTSALE
    )
  );
  // eslint-disable-next-line
  const [holdPermission, setHoldPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTHOLD
    )
  );

  // eslint-disable-next-line
  const [offerPermission, setOfferPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTOFFER
    )
  );

  // eslint-disable-next-line
  const [relabPermission, setRelabPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTRELAB
    )
  );

  // eslint-disable-next-line
  const [offlinePermission, setOfflinePermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTOFFLINE
    )
  );

  // eslint-disable-next-line
  const [mixPermission, setMixPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTMIX
    )
  );

  // eslint-disable-next-line
  const [fullExcelPermission, setFullExcelPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECT_EXCEL_DOWNLOAD_FULL
    )
  );

  useEffect(() => {
    if (stockLoader || watchlistLoader || offlineLoader) {
      setDisableAllButtons(true);
    } else {
      setDisableAllButtons(false);
    }
  }, [stockLoader, watchlistLoader, offlineLoader]);

  useEffect(() => {
    PageTitle("OPERATION_STOCK");
    if (guid) {
      StoneProxy.GetCriteriaByGUID(guid, (response) => {
        if (response) {
          setStockCriteriaState(JSON.parse(response.criteria));
          setDrawerExpanded(true);
        } else {
          setShowSuccessSnackbar(true);
          setSuccessMessage(Constants.APiFailErrorMessage);
          setSnackbarVariant("error");
        }
      })
    } else {
      setDrawerExpanded(true);
    }
  }, []);

  useEffect(() => {

    let stockMap = new Map();
    if (stockData.length !== 0) {
      if (selectionModel.length <= 0)
        loadTotalData(stockData);
      stockData.forEach(iData => {
        stockMap.set(iData.stoneid, iData);
      })
      setStoneDataMap(stockMap);
    }
  }, [stockData])

  useEffect(() => {

    if (showNewStone) {
      setStockData(newStoneList);
    } else {
      setStockData(allStoneList);
    }
  }, [showNewStone])

  const goToFirstPage = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const prepareData = (responseData) => {

    const newStoneList = [];
    responseData.map(row => {
      const today = new Date();
      const nDaysAgo = new Date(today);
      nDaysAgo.setDate(today.getDate() - Constants.NEW_STONE_DAYS);

      const referenceDate = row.age ? new Date(row.age) : row.createdat ? new Date(row.createdat) : null;

      row.isNew = referenceDate ? referenceDate > nDaysAgo : false;

      if (row.isNew) {
        newStoneList.push(row);
      }
      return true;
    });

    setNewStoneList(newStoneList);
    setAllStoneList(responseData);
    setStockData(showNewStone ? newStoneList : responseData);
  }

  const handleSearch = (data, stockCriteriaStored) => {


    if (!stockCriteriaStored)
      return;

    let activeTabValue = JSON.parse(stockCriteriaStored).activeTab;
    let stoneTypeFilters = activeTabValue === "Natural" ? ["Natural"] : Constants.uniqueFilterValues.stonetype.filter(option => option.name !== 'N').map(option => option.fullname);

    data.status = [];
    data.status.push('AVAILABLE');
    data.status.push('HOLD');
    data.status.push('MEMO_OUT');
    if (Utility.isReadAllowed(offlinePermission))
      data.status.push('OFFLINE');

    StoneProxy.GetFilteredStockSearch(
      Constants.loggedInUser.id,
      customerId.value,
      data,
      (responseData) => {
        if (responseData) {
          const distinctStatus = Utility.getDistinctOptions(responseData, 'status');
          prepareColumns(distinctStatus, stoneTypeFilters);
          setSortModel([]);
          prepareData(responseData);
          setAllStoneList(responseData);
          setStockLoader(false);
        } else {
          setSortModel([]);
          setStockLoader(false);
          setErrorSnackbar(true);
        }
      }
    );

    setActiveTabValue(activeTabValue)
    goToFirstPage();
    setSearchCriteria(data);
    setStockCriteriaState(stockCriteriaStored)
    setStockLoader(true);
  };

  // eslint-disable-next-line
  const checkStoneStatusIfValidOrNotToCreateMemo = () => {
    let stoneDataArray = [];
    let differentCustomerid;
    let differentCustomerCompanyName;
    let flag = false;
    selectionModel.map(stoneid => {
      let iData = stoneDataMap.get(stoneid);
      if (iData.status !== "AVAILABLE" && iData.status !== "MEMO_REQUESTED" && iData.status !== "MEMO_OUT" &&
        ((iData.stoneoldstatus === "HOLD" || iData.status === "HOLD") && (
          (Constants.loggedInUser.usertype === 'C' && iData.holdstoneuserid !== Constants.loggedInUser.id) ||
          (Constants.loggedInUser.usertype === 'B' && iData.holdstoneuserid !== Constants.loggedInUser.id) ||
          (Constants.loggedInUser.usertype === 'E' && iData.holdstoneuserid !== Constants.loggedInUser.id && !customerOptions.includes(iData.holdstonecustomerid)) ||
          iData.holdstonecustomerid !== customerId.value
        ))) {
        stoneDataArray.push(iData.stoneid);
      } else if (!flag && iData.holdstonecustomerid !== null) {
        differentCustomerid = iData.holdstonecustomerid;
        differentCustomerCompanyName = iData.stoneholdcustomercompanyname;
        flag = true;
      } else if (iData.holdstonecustomerid !== null && differentCustomerid !== iData.holdstonecustomerid) {
        stoneDataArray.push(iData.stoneid);
      }
      return stoneDataArray;
    })

    if (stoneDataArray.length === 0) {
      setShow(!show);
      setCustomerId(flag ? { label: differentCustomerCompanyName, value: differentCustomerid } : customerId);
    }
    else if (stoneDataArray.length > 0) {
      setWarning(true);
      setWarningMessage('To perform this action, Please select stones with status "AVAILABLE" OR the stone which you have "HOLD"')
    }

  };

  const checkStoneOffline = () => {
    let stoneDataArray = [];
    selectionModel.map(stoneid => {
      let iData = stoneDataMap.get(stoneid);
      if (iData.status === "OFFLINE") {
        stoneDataArray.push(iData.stoneid);
      }
      return true;
    });
    if (stoneDataArray.length === 0) {
      return false;
    } else if (stoneDataArray.length > 0) {
      setWarning(true);
      setWarningMessage('You cannot perform any action on Stones with status "OFFLINE".')
    }
    return true;
  }

  const handleOpen = () => {
    setShow(!show);

    // We are not using the below function anymore
    // as we are allowing all the stones with any status
    // to be MEMO_REQUESTED
    // checkStoneStatusIfValidOrNotToCreateMemo();
  };

  const handleValidation = (customer) => {
    //TODO do validation if needed here
    return true;
  };

  const prepareColumns = (distinctStatus, stoneTypeFilter) => {
    let columnNames = [];

    Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
      if (columnsData.hasOwnProperty(iColPermission)) {
        const columnData = columnsData[iColPermission];
        if (columnData.field === "status") {
          columnData.type = "singleSelect";
          columnData.valueOptions = distinctStatus;
          columnData.filterOperators = Utility.combinedFilters();
        }
        if (columnData.field === "stonetype") {
          columnData.type = "singleSelect";
          columnData.valueOptions = stoneTypeFilter;
          columnData.filterOperators = Utility.combinedFilters();
          columnData.valueGetter = (params) => Constants.STONE_TYPE[params.value];
          columnData.renderCell = (params) => Constants.STONE_TYPE[params.value];
        }
        columnNames.push(columnData);
      }
    });

    setColumnNames(columnNames);
  }

  const validateRelabStones = () => {

    let stoneDataArray = [];
    selectionModel.map(stoneid => {
      let iData = stoneDataMap.get(stoneid);
      if (iData.status !== "AVAILABLE" || iData.stoneoldstatus !== null) {
        stoneDataArray.push(iData.stoneid);
      }
      return true;
    })
    if (stoneDataArray.length === 0) {
      setShowRelabModal(true);
    }
    else if (stoneDataArray.length > 0) {
      setWarning(true);
      setWarningMessage('To perform this action, Please select stones with the status "AVAILABLE". If the status is already "AVAILABLE", the stone may be in "LOCATION_TRANSFER".');
    }
  }

  const createMemo = (data) => {
    setLoadingButton(true);
    const usertype = Constants.loggedInUser.usertype;
    StoneProxy.CreateMemo(data, usertype, (responseData) => {
      if (responseData && responseData.status === "Success") {
        handleSearch(searchCriteria, filters);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Memo created successfully");
        setSnackbarVariant("success");
        setShow(false);
        setLoadingButton(false);
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
    });
  };

  const createProforma = (data) => {
    setLoadingProformaButton(true);
    StoneProxy.CreatePerforma(data, (responseData) => {
      if (responseData && responseData.status === "Success") {
        handleSearch(searchCriteria, filters);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Proforma created successfully");
        setSnackbarVariant("success");
        setShowSellModal(false);
        setLoadingProformaButton(false);
      } else {
        setShowSuccessSnackbar(true);
        setShowSellModal(false);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingProformaButton(false);
      }
    });
  };

  const handleClose = () => {
    setWarning(false);
    setShowStoneDetailModal(false);
    setShowSuccessSnackbar(false);
    setShowPacketDetailModal(false);
    setShowReleaseModal(false);
  };

  const sellStonesInvoice = (data) => {
    setLoadingButton(true);
    StoneProxy.SellStonesInvoice(data, (responseData) => {
      if (responseData && responseData.status === "Success") {
        handleSearch(searchCriteria, filters);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stones sold successfully");
        setSnackbarVariant("success");
        setShowSellModal(false);
        setLoadingButton(false);
      } else {
        setShowSuccessSnackbar(true);
        setShowSellModal(false);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
    });
  };

  const createOffer = (data) => {
    setLoadingButton(true);
    const usertype = Constants.loggedInUser.usertype;
    StoneProxy.CreateOffer(data, usertype, (responseData) => {
      if (responseData) {
        handleSearch(searchCriteria, filters);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Offer created successfully");
        setSnackbarVariant("success");
        setShowOfferModal(false)
        setLoadingButton(false);
      } else {
        setShowSuccessSnackbar(true);
        setShowOfferModal(false)
        setSuccessMessage(Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
    });
  };

  const createPdf = () => {

    let criteria = JSON.parse(stockCriteriaState);

    const pdfcolumns = [
      "REPORT", "SHAPE", "ID", "CTS", "COL", "CLA", "CUT", "POL", "SYM", "FLR",
      "DIS", "PPC", "LAB", "MEASUREMENTS", "DEP%", "TAB%", "RAP", "TOTAL"
    ];

    let headerColumn = criteria && criteria.colorWhite ? pdfcolumns : pdfcolumns.filter(i => i !== "RAP" && i !== "DIS");

    let data = [];

    if (selectionModel && selectionModel.length > 0) {
      data = stockData.filter((data) => selectionModel.includes(data.stoneid));
    } else {
      data = stockData;
    }

    let noOfStones = 0;
    let totalSize = 0;
    let totalAmount = 0;
    let total = 0;

    const pdfData = data.map(item => {
      noOfStones += 1;

      if (Utility.isValueValid(item.size)) {
        totalSize += Number(item.size);
      }

      if (Utility.isValueValid(item.total)) {
        totalAmount += Number(item.total);
      }

      if (
        Utility.isValueValid(item.price) &&
        Utility.isValueValid(item.discount) &&
        Utility.isValueValid(item.size)
      ) {
        const effectivePrice = (100 * Number(item.price)) / (100 + Number(item.discount));
        total += Number(item.size) * effectivePrice;
      }

      return {
        REPORT: item.certificateid,
        SHAPE: item.shape,
        ID: item.stoneid,
        CTS: Number(item.size).toFixed(2),
        COL: item.colortype === "WHITE" ? item.color : item.colordescriptions,
        CLA: item.clarity,
        CUT: item.cut,
        POL: item.pol,
        SYM: item.sym,
        FLR: item.flo,
        DIS: Number(item.discount).toFixed(2),
        PPC: Number(item.price).toFixed(2),
        LAB: item.lab,
        MEASUREMENTS: item.measurements,
        "DEP%": Number(item.depthpercent).toFixed(2),
        "TAB%": Number(item.tablepercent).toFixed(2),
        RAP: Number(item.rapnetprice),
        TOTAL: Number(item.total).toFixed(2),
      };
    });

    pdfData.push({
      ID: noOfStones,
      CTS: (Number(totalSize)).toFixed(2),
      DIS: (Number((100 - (totalAmount / total) * 100) * -1)).toFixed(2),
      PPC: (Number(totalAmount / totalSize)).toFixed(2),
      TOTAL: (Number(totalAmount)).toFixed(2),
    })

    Utility.downloadPDF(headerColumn, pdfData, "stock.pdf");
  };

  const getSummaryData = () => {

    let data = [];
    if (selectionModel && selectionModel.length > 0) {
      data = stockData.filter((data) => selectionModel.includes(data.stoneid));
    } else {
      data = stockData;
    }

    let noOfStones = 0;
    let totalSize = 0;
    let totalAmount = 0;
    let total = 0;

    data.forEach(item => {
      noOfStones += 1;

      if (Utility.isValueValid(item.size)) {
        totalSize += Number(item.size);
      }

      if (Utility.isValueValid(item.total)) {
        totalAmount += Number(item.total);
      }

      if (
        Utility.isValueValid(item.price) &&
        Utility.isValueValid(item.discount) &&
        Utility.isValueValid(item.size)
      ) {
        const effectivePrice = (100 * Number(item.price)) / (100 + Number(item.discount));
        total += Number(item.size) * effectivePrice;
      }
    });

    return {
      ID: noOfStones,
      CTS: (Number(totalSize)).toFixed(2),
      DIS: (Number((100 - (totalAmount / total) * 100) * -1)).toFixed(2),
      PPC: (Number(totalAmount / totalSize)).toFixed(2),
      TOTAL: (Number(totalAmount)).toFixed(2),
    };

  }

  const getExcelData = (fullExcel) => {
    let data = [];

    if (selectionModel && selectionModel.length > 0) {
      data = stockData.filter((data) => selectionModel.includes(data.stoneid));
    } else {
      data = stockData;
    }

    const excelData = data.map(item => {

      return {
        REPORT: item.certificateid,
        SHAPE: item.shape,
        ID: item.stoneid,
        CTS: item.size,
        COL: item.colortype === "WHITE" ? item.color : item.colordescriptions,
        CLA: item.clarity,
        CUT: item.cut ? item.cut : " ",
        POL: item.pol,
        SYM: item.sym,
        FLR: item.flo,
        MEASUREMENTS: item.measurements,
        DEP: !item.depthpercent ? "" : (Number(item.depthpercent)).toFixed(2),
        "TABLE%": !item.tablepercent ? "" : (Number(item.tablepercent)).toFixed(2),
        "CR ANG": !item.crownangle ? "" : (Number(item.crownangle)).toFixed(2),
        LAB: item.lab,
        RAP: item.rapnetprice,
        DIS: parseFloat((Number(item.discount)).toFixed(2)),
        PPC: parseFloat((Number(item.price)).toFixed(2)),
        TOTAL: parseFloat((Number(item.total)).toFixed(2)),
        LOCATION: item.location,
        STATUS: item.status,
        KEYTOSYMBOLS: item.keytosymbols ? item.keytosymbols : " ",
        MEDIA: {
          t: "s",
          v: "Media URL",
          l: {
            Target: item.stonemediaurl,
            Tooltip: "Click to open images & videos",
          },
        },
        status: item.status ? item.status : "",
        holdstonecustomerid: item.holdstonecustomerid,
        holdstoneuserid: item.holdstoneuserid,
        holdstonebrokerid: item.holdstonebrokerid,
        TABLEINC: item.tableinc ? item.tableinc : "",
        SIDEINC: item.sideinc ? item.sideinc : "",
        H_AND_A: item.h_and_a ? item.h_and_a : "",
        EYECLEAN: item.eyeclean ? item.eyeclean : "",
        TINGE: item.tinge ? item.tinge : "",
        MILKY: item.milky ? item.milky : "",
        OPEN: item.open ? item.open : "",
        LUSTER: item.luster ? item.luster : "",
        TABLEBLACK: item.tableblack ? "YES" : "NO",
        SIDEBLACK: item.sideblack ? "YES" : "NO",
      };
    });

    const calculatedRow = getSummaryData();

    //addding last row for summary data
    const lastRow = Object.fromEntries(
      Constants.STOCK_EXCEL_KEYS.map(key => [key, calculatedRow[key] || ""])
    );

    //addding last row for summary data
    excelData.push(lastRow);

    const exportColumns = fullExcel
      ? [...Constants.STOCK_EXCEL_COLUMNS, ...Constants.EXCEL_OPTIONAL_COLUMNS]
      : Constants.STOCK_EXCEL_COLUMNS;

    createExcelFile(excelData, customerId, exportColumns, "PI-Stock.xlsx");
  }

  const handleStoneDetailModal = (params) => {
    setStoneId(params.value);
    setShowStoneDetailModal(true);
  };

  const holdStones = (data) => {
    setLoadingButton(true);
    const usertype = Constants.loggedInUser.usertype;
    StoneProxy.HoldStones(data, usertype, (responseData) => {
      if (responseData && responseData.status === "Success") {
        handleSearch(searchCriteria, filters);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Hold Successful");
        setSnackbarVariant("success");
        setShowHoldModal(false);
        setLoadingButton(false);
        setSelectionModel([]);
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(responseData.status === "Error" ? "Unable to hold the stone. Please refresh the page and try again." : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setShowHoldModal(false);
        setLoadingButton(false);
      }
    });
  };

  const relabStones = (data) => {
    setLoadingButton(true);
    StoneProxy.UpdateStoneStatus(data, "RE-LAB", (responseData) => {
      if (responseData && responseData.status === "Success") {
        handleSearch(searchCriteria, filters);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stone sent to RE-LAB successfully");
        setSnackbarVariant("success");
        setShowRelabModal(false)
        setLoadingButton(false);
      } else {
        setShowSuccessSnackbar(true);
        setShowRelabModal(false)
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
    });
  };

  const releaseStone = (data) => {
    setLoadingButton(true);
    StoneProxy.ReleaseStones(data, (responseData) => {
      if (responseData && responseData.status === "Success") {
        handleSearch(searchCriteria, filters);
        setLoadingButton(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stone Release Successfully");
        setSnackbarVariant("success");
        setSelectionModel([]);
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
      setShowReleaseModal(false);
    });
  };

  const loadTotalData = (data) => {
    let totalSize = 0;
    let totalPrice = 0;   //ppc*size
    let total = 0;       //rapnetprice*size

    data.forEach((iData) => {
      let pricePerCarat = iData.price
      totalSize += iData.size;
      totalPrice += (iData.size * pricePerCarat);
      total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount))); // total price after applying diiscount 

    });
    setNoOfSelectedStones(data.length);
    setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
    setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
    setSelectedavgPrice(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          totalPrice / totalSize
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
    setSelectedAvgDiscount(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          (100 - (totalPrice / total) * 100) * -1
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
  };

  useEffect(() => {
    if (selectionModel.length <= 0) {
      loadTotalData(stockData);
      return;
    }
    let totalSize = 0;
    let totalPrice = 0;   //ppc*size
    let total = 0;       //rapnetprice*size
    stockData.forEach((iData) => {
      if (selectionModel.includes(iData.stoneid)) {
        let pricePerCarat = iData.price
        totalSize += iData.size;
        totalPrice += (iData.size * pricePerCarat);
        total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
      }
    });
    setNoOfSelectedStones(selectionModel.length);
    setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
    setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
    setSelectedavgPrice(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          totalPrice / totalSize
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
    setSelectedAvgDiscount(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          (100 - (totalPrice / total) * 100) * -1
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );

  }, [selectionModel]);

  const handlePacketDetailModal = (params, kapanid) => {
    setPacketId(params);
    setKapanId(kapanid);
    setShowPacketDetailModal(true);
  };

  const addToWatchlist = (data) => {
    setWatchlistLoader(true);
    WatchlistProxy.AddToWatchlist(data, (res) => {
      if (res && res.status === "Success") {
        //Success
        setShowWatchlistModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Watchlist Added successfully");
        setSnackbarVariant("success");
        setWatchlistLoader(false);
      } else {
        // Failure
        setShowWatchlistModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage(res ? res.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setWatchlistLoader(false);
      }
    })
  }

  const handleMixBucketStoneSave = (data) => {
    setLoadingButton(true);
    LabTestProxy.AddStonesToMix(data, (responseData) => {
      if (responseData && responseData.status === "Success") {
        handleSearch(searchCriteria, filters);
        setShowMixBucketModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stone sent to Mix");
        setSnackbarVariant("success");
        setLoadingButton(false);
      } else {
        setShowMixBucketModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
    });
  }

  const handleSendMessage = (textmessage) => {
    let customerid = rowData.holdstonecustomerid;
    let userid = Constants.loggedInUser.id;
    StoneProxy.sendHoldStoneCustomerEmployeeNotification(userid, customerid, textmessage, (response) => {
      if (response) {
        setMessageModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Notification sent successfully");
        setSnackbarVariant("success");
        setShowSellModal(false);
      } else {
        setMessageModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage(Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
      }
    });
  };

  const createMessage = (data) => {
    let message = "Stone ID " + data.stoneid + " is currently on hold for your customer. Urgently requesting its release as a potential buyer is interested.";
    setMessage(message);
    setRowData(data);
    setMessageModal(true);
  };

  const generateLink = () => {
    setUploadLoader(true);

    let jsonCriteria = JSON.parse(stockCriteriaState);
    jsonCriteria.customerOption = [];
    jsonCriteria.savedSearchOption = [];
    jsonCriteria.selectedSavedSearch = { label: 'Select', value: -1 };
    jsonCriteria.savedSearchCriteria = {};
    jsonCriteria.previousSearchOption = [];
    jsonCriteria.selectedPreviousSearch = { label: 'Select', value: -1 };

    let data = {
      createdby: Constants.loggedInUser.id,
      customerid: customerId.value,
      criteria: JSON.stringify(JSON.stringify(jsonCriteria))
    }
    StoneProxy.GenerateCriteriaLink(data, (response) => {
      if (response) {
        setShowSuccessSnackbar(true);
        setSuccessMessage("Link generated successfully");
        setSnackbarVariant("success");
        setUploadLoader(false);
        setGeneratedLink(response.guid);
        setShowCriteriaLinkModal(true);
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setUploadLoader(false);
      }
    })
  }

  const markStoneStatusToOffline = () => {
    setOfflineLoader(true);
    StoneProxy.MarkStoneStatusToOffline(selectionModel, response => {
      if (response && response.status === "Success") {
        handleSearch(searchCriteria, filters);
        setShowSuccessSnackbar(true);
        setSuccessMessage(response.errorMessage);
        setSnackbarVariant("success");
        setOfflineLoader(false);
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(response ? response.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setOfflineLoader(false);
      }
    })
  }

  const markStoneStatusToAvailable = () => {
    setOfflineLoader(true);
    StoneProxy.MarkStoneStatusToAvailable(selectionModel, response => {
      if (response && response.status === "Success") {
        handleSearch(searchCriteria, filters);
        setShowSuccessSnackbar(true);
        setSuccessMessage(response.errorMessage);
        setSnackbarVariant("success");
        setOfflineLoader(false);
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(response ? response.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setOfflineLoader(false);
      }
    })
  }

  const columnsData = {
    "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
      field: "stoneid",
      headerName: "STONEID",
      pinning: { position: 'left' },
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 95,
      cellClassName: 'stickyColumn',
      renderCell: (params) => {
        return Utility.NeedToShowStoneDetailModal() ?
          (
            params.row.isNew ? (
              <Box
                sx={{
                  "& .MuiBadge-badge": {
                    height: "0.75rem !important",
                    padding: "2px 4px !important",
                    fontSize: "10px",
                    top: "-3px",
                    backgroundColor: "#E0FBE2 !important",
                    color: "#059212 !important",
                  },
                }}
              >
                <Badge
                  badgeContent={"NEW"}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <Button
                    sx={{
                      textDecorationLine: "underline",
                      color: 'black',
                      height: '5px',
                      marginRight: '2px',
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                  >
                    {params.value}
                  </Button>
                </Badge>
              </Box>
            ) : (
              <Button
                sx={{
                  textDecorationLine: "underline",
                  color: 'black',
                }}
                onClick={() => handleStoneDetailModal(params)}
              >
                {params.value}
              </Button>
            )
          ) : params.row.isNew ? (
            <Box
              sx={{
                "& .MuiBadge-badge": {
                  height: "0.75rem !important",
                  padding: "2px 4px !important",
                  fontSize: "10px",
                  top: "-3px",
                  backgroundColor: "#E0FBE2 !important",
                  color: "#059212 !important",
                },
              }}
            >
              <Badge
                badgeContent={"NEW"}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Button
                  sx={{
                    color: 'black',
                    height: '5px',
                    marginRight: '2px',
                    '&.Mui-disabled': {
                      color: 'black',
                      // opacity: 1,
                    },
                  }}
                  disabled
                >
                  {params.value}
                </Button>
              </Badge>
            </Box>
          ) : <div> {params.value}</div>;
      }
    },
    "e24542ae-7b98-44f2-82be-09dfa2dd850b": {
      field: "mumid",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      width: 90,
      renderCell: (params) => (!params.value) ? "" : params.value,
    },
    "7a913460-41e0-441d-85b9-1f33d71416fb": {
      field: "suratid",
      headerName: "REF NO",
      headerAlign: "center",
      align: "center",
      width: 90,
      renderCell: (params) => (!params.value) ? "" : params.value,
    },
    "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
      field: "kapanid",
      headerName: "KAPANID",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
        <Button
          sx={{
            textDecorationLine: "underline",
            color: 'black',
          }}
          onClick={() => {
            setKapanId(params.row.kapanid);
            setShowKapanDetailModal(true);
          }}
        >
          {params.value}
        </Button>
        :
        <div>{params.value}</div>,
      width: 80,
    },
    "8a492e00-6167-45de-b078-e572e47c8a39": {
      field: "packetid",
      headerName: "PACKETID",
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => params.value,
      width: 80,
      renderCell: (params) => {
        if (params.value === null || params.row.kapanid === null) {
          return;
        } else {
          return Utility.NeedToShowPacketDetailModal() ? (
            <Button
              sx={{ textDecorationLine: "underline", color: "black" }}
              onClick={() => handlePacketDetailModal(params.value, params.row.kapanid)}
            >
              {params.value}
            </Button>
          ) : (
            <div>{params.value}</div>
          );
        }
      }
    },
    "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
      field: "status",
      headerName: "STATUS",
      headerAlign: "center",
      align: "center",
      width: 180,
      renderCell: (params) => {
        if (Constants.loggedInUser.usertype === "E" && params.value === "HOLD" && params.row.holdstoneuserid !== Constants.loggedInUser.id && !customerOptions.includes(params.row.holdstonecustomerid)) {
          return (
            <>
              <div style={{ display: 'flex', marginLeft: 5 }}>
                <span style={{ fontSize: "14px" }}><b>{params.value}</b></span>
                <MailOutlineIcon onClick={() => createMessage(params.row)}
                  style={{
                    fontSize: '1.2rem', marginLeft: 6, marginBottom: 3,
                    textAlign: "right ! important", cursor: "pointer",
                  }} />
                {params.row.memocustomerid ? (
                  <span style={{ fontSize: "14px" }}>&nbsp; |&nbsp;MEMO_OUT</span>
                ) : null}
              </div>
            </>
          );
        } else {
          return (
            params.value === "HOLD" ? (
              <span style={{ fontSize: "14px" }}>
                <b>{params.value}</b>
                {params.row.memocustomerid ? (<>&nbsp; |&nbsp;MEMO_OUT</>) : null}
              </span>
            ) : <span>{params.value}</span>
          );
        }
      },
      cellClassName: (params) =>
        (params.row.status === "HOLD" && params.row.holdstonecustomerid === customerId.value && (
          // customerOption Check is to allow Employee to do the Processing if the Customer has Hold the stone
          params.row.holdstoneuserid === Constants.loggedInUser.id || (Constants.loggedInUser.usertype === "E" && customerOptions.includes(params.row.holdstonecustomerid))
        )) ||
          (params.row.status === "MEMO_OUT" && params.row.memocustomerid === customerId.value && (
            params.row.memouserid === Constants.loggedInUser.id || (Constants.loggedInUser.usertype === "E" && customerOptions.includes(params.row.memocustomerid))
          )) ? 'greenCell' :
          (params.row.status === "HOLD" && params.row.holdstonecustomerid !== customerId.value && (
            params.row.holdstoneuserid === Constants.loggedInUser.id || (Constants.loggedInUser.usertype === "E" && customerOptions.includes(params.row.holdstonecustomerid))
          )) ||
            (params.row.status === "MEMO_OUT" && params.row.memocustomerid !== customerId.value && (
              params.row.memouserid === Constants.loggedInUser.id || (Constants.loggedInUser.usertype === "E" && customerOptions.includes(params.row.memocustomerid))
            )) ? 'yellowCell' : `super-app-theme--${params.row.status.toUpperCase()}`,
    },
    "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
      field: "stonetype",
      headerName: "STONETYPE",
      headerAlign: "center",
      align: "center",
      width: 100,
    },
    "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
      field: "shape",
      headerName: "SHAPE",
      headerAlign: "center",
      align: "center",
      width: 80,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.shape.map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
      field: "size",
      headerName: "SIZE",
      headerAlign: "center",
      width: 40,
      type: "number",
      valueFormatter: (params) => params.value,
      align: "right",
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
    },
    "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
      field: "color",
      headerName: "COLOR",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 65,
      renderCell: renderCellExpand,
    },
    "d4992de9-92ab-4f9e-ba12-6c746e935611": {
      field: "colordescriptions",
      headerName: "COLOR DESCRIPTIONS",
      headerAlign: "center",
      align: "center",
      width: 160,
      renderCell: renderCellExpand,
    },
    "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
      field: "clarity",
      headerName: "CLARITY",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 75,
      renderCell: renderCellExpand,
    },
    "3597803f-8932-40c6-8de6-81006f4d6d5b": {
      field: "cut",
      headerName: "CUT",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 50,
      renderCell: renderCellExpand,
    },
    "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
      field: "pol",
      headerName: "POL",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 40,
      renderCell: renderCellExpand,
    },
    "5541c182-21b8-496e-b342-b0b89f3d302f": {
      field: "sym",
      headerName: "SYM",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 40,
      renderCell: renderCellExpand,
    },
    "8296e627-0be5-4a66-830f-ea6867cea137": {
      field: "flo",
      headerName: "FLO",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 60,
      renderCell: renderCellExpand,
    },
    "798a8663-0f0c-41be-8512-16dd60d5f25e": {
      field: "discount",
      headerName: "DIS",
      headerAlign: "center",
      align: "right",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 50,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
    },
    "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
      field: "rapnetprice",
      headerName: "RAP",
      headerAlign: "center",
      align: "right",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
    },
    "39c634f1-2644-4307-acbb-983b4de4229e": {
      field: "price",
      headerName: "PPC",
      headerAlign: "center",
      align: "right",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
    },
    "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
      field: "total",
      headerName: "TOTAL",
      headerAlign: "center",
      align: "right",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
    },
    "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
      field: "measurements",
      headerName: "MEASUREMENTS",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 140,
      renderCell: renderCellExpand,
    },
    "567202e6-c31c-4c19-9998-1cacae05af6e": {
      field: "depth",
      headerName: "DEPTH",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: renderCellExpand,
    },
    "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
      field: "lab",
      headerName: "LAB",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 40,
      renderCell: renderCellExpand,
    },
    "19ce7030-402c-495c-84ac-a29093bdb0ac": {
      field: "locationid",
      headerName: "LOCATION",
      headerAlign: "center",
      type: "number",
      align: "center",
      valueFormatter: (params) => params.value,
      width: 90,
      renderCell: renderCellExpand,
    },
    "ab3e506e-bddb-456d-b967-922dcee74d56": {
      field: "location",
      headerName: "LOCATION",
      headerAlign: "center",
      align: "left",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 130,
      renderCell: (params) => params.row.stoneoldstatus === null ? params.value : (
        <Box
          sx={{
            "& .MuiBadge-badge": {
              height: "0.75rem !important",
              padding: "2px 4px !important",
              fontSize: "8px",
              top: "-1px",
              right: "-20px",
              backgroundColor: "#E5D9F2 !important",
              color: "#624E88 !important",
              fontWeight: "700"
            },
          }}
        >
          <Badge
            badgeContent={"TRANSIT"}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {params.value}
          </Badge>
        </Box>
      ),
    },
    "f97a0118-3891-4f77-abf7-8636c26d594c": {
      field: "country",
      headerName: "COUNTRY",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
      field: "quality",
      headerName: "QUALITY",
      headerAlign: "center",
      align: "center",
      width: 80,
      renderCell: renderCellExpand,
    },
    "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
      field: "shade",
      headerName: "SHADE",
      headerAlign: "center",
      align: "center",
      width: 60,
      renderCell: renderCellExpand,
    },
    "01e90aca-bdcb-4e8e-80ec-2d7007df8cd3": {
      field: "createdat",
      headerName: "CREATED AT",
      headerAlign: "center",
      align: "center",
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
      field: "height",
      headerName: "HEIGHT",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: renderCellExpand,
    },
    "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
      field: "width",
      headerName: "WIDTH",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: renderCellExpand,
    },
    "8eaa8a03-d163-436d-8912-51ea06306f44": {
      field: "certificateid",
      headerName: "CERT",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 100,
      renderCell: renderCellExpand,
    },
    "77e48fa7-d118-4ab8-865b-9b85b32a004f": {
      field: "fancycolor1",
      headerName: "F.COLOR",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "ccba9873-174e-4060-963a-9893c3625bb2": {
      field: "fancycolorovertone",
      headerName: "OVERTONE",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "c9aa6ee7-30e3-423e-a534-80bb5ef7bbaf": {
      field: "fancycolorintensity",
      headerName: "F.COL.INTENSITY",
      headerAlign: "center",
      align: "center",
      width: 140,
      renderCell: renderCellExpand,
    },
    "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
      field: "comment",
      headerName: "COMMENT",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "e7210f4a-6e0a-4591-8428-e6845004ed04": {
      field: "crownheight",
      headerName: "CR.HEIGHT",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "c4e65984-1682-4c1e-812b-8938e9078b2d": {
      field: "crownangle",
      headerName: "CR.ANG",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
      field: "culetsize",
      headerName: "CULET SZ",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "e3f77521-2c57-46bc-a02b-3c884685d2be": {
      field: "culetcondition",
      headerName: "CULET",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
      field: "depthpercent",
      headerName: "DEPTH%",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 75,
      renderCell: renderCellExpand,
    },
    "e83c2978-9088-49f0-9dc1-bcced73025fd": {
      field: "gridlemin",
      headerName: "GIRDLEMIN",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
      field: "intensitylongtitle",
      headerName: "INTENSITYLONGTITLE",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "06d2a589-0d42-4dde-8724-acf2448c839a": {
      field: "laserinscription",
      headerName: "INS",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "dd934505-1865-48cb-878f-f5d3c18af2d5": {
      field: "note",
      headerName: "NOTE",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
      field: "paviliondepth",
      headerName: "PV.DEPTH",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
      field: "tablepercent",
      headerName: "TABLE%",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 75,
      renderCell: renderCellExpand,
    },
    "db078198-4416-4d53-b3d0-70d3a5418f65": {
      field: "open",
      headerName: "OPEN",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "e9028170-e978-4978-a2c9-1889c1821495": {
      field: "gridlecondition",
      headerName: "GIRDLE COND",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
      field: "gridlepercent",
      headerName: "GIRDLE%",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
      field: "pavilionangle",
      headerName: "PV.ANGLE",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
      field: "white",
      headerName: "WHITE",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "24ba0bf8-4ca1-4726-ad90-19a62c8c72e6": {
      field: "boxid",
      headerName: "BOXID",
      headerAlign: "center",
      width: 60,
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "cf694b75-ba5d-4a73-8fcf-e7566974c4b6": {
      field: "media",
      headerName: "MEDIA",
      headerAlign: "center",
      align: "center",
      width: 60,
      renderCell: (params) => (
        <Tooltip title={"Stone Media"} sx={{ cursor: "pointer" }}>
          <VideoLibraryTwoToneIcon
            sx={{ cursor: "pointer" }}
            onClick={() => {
              setStoneMediaURL(params.row.stonemediaurl);
              setShowStoneMediaModal(true);
            }} />
        </Tooltip>
      ),
    },
    "f3223657-c8d2-449c-b2f5-5a54c78401ce": {
      field: "tableinc",
      headerName: "TABLE INC",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 120,
    },
    "f114ad2c-8560-4da7-9b03-03ba6bfe39a5": {
      field: "sideinc",
      headerName: "SIDE INC",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "e993a4f7-7c50-4d83-abae-fccc03382742": {
      field: "h_and_a",
      headerName: "H&A",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "f2fe7666-6489-40ce-b4b7-040c68c88bf3": {
      field: "eyeclean",
      headerName: "EYE CLEAN",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "b38b9fdf-bfb8-43c6-82a2-1e366fa71523": {
      field: "tinge",
      headerName: "TINGE",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.tinge.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 120,
    },
    "c95089ee-166d-4783-817c-2638f6d40df1": {
      field: "milky",
      headerName: "MILKY",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.milky.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 120,
    },
    "2a6475ff-481f-48c6-9e92-3ede0b97d7c0": {
      field: "luster",
      headerName: "LUSTER",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "556ae429-3605-4df4-88d8-b5223fc2e2fb": {
      field: "tableblack",
      headerName: "TABLE BLACK",
      headerAlign: "center",
      align: "center",
      type: "boolean",
      width: 120,
    },
    "5c70d1ca-2a7b-4660-8160-0beff7ad1150": {
      field: "sideblack",
      headerName: "SIDE BLACK",
      headerAlign: "center",
      align: "center",
      type: "boolean",
      width: 120,
    },
    "a1299782-d545-4d70-bcbc-7d576e396d64": {
      field: "length",
      headerName: "LENGTH",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 120,
    },
    "dfd5488a-b600-4e75-9735-98c26f78dc84": {
      field: "strln",
      headerName: "STAR LENGTH",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "66dfbc62-477f-42f0-8097-56c5d8329156": {
      field: "lrhalf",
      headerName: "LR HALF",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 120,
    },
    "e683145c-29dd-4d41-af1a-518ff80090d2": {
      field: "keytosymbols",
      headerName: "KEY TO SYMBOLS",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "e32fe363-07cc-4307-82d2-82ae1069b5be": {
      field: "age",
      headerName: "AGE",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      valueGetter: (params) => {
        if (params.value)
          return Utility.ConvertDateToAge(params.value);
        else
          return null;
      },
      renderCell: (params) => {
        return params.value;
      },
    }
  };

  const rows = stockData;
  const columns = columnNames;

  const allowed = Utility.isReadAllowed(local_Permission);

  // Color Scheme Drop Down Button
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseButton = () => {
    setAnchorEl(null);
  };

  //MARK: Custom Tool Bar
  function CustomToolbar() {
    const [exportMenu, setExportMenu] = useState(undefined);
    const [offlineMenu, setOfflineMenu] = useState(undefined);
    const [holdMenu, setHoldMenu] = useState(undefined);

    const buttonMinWidth = Utility.isReadAllowed(fullExcelPermission) ? "130px" : "100px";
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          alignItems: "center",
          marginHorizontal: "5px",
          "&.MuiDataGrid-toolbarContainer": {
            padding: "0px 4px !important",
          },
        }}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />

        {/* Custom Buttons */}
        {searchCriteria === "" ? (
          <Box />
        ) : stockLoader ? (
          <CircularProgress size="1rem" />
        ) : (
          <Tooltip title="Reload List">
            <RefreshOutlinedIcon
              onClick={() =>
                searchCriteria && customerId.value
                  ? handleSearch(searchCriteria, filters)
                  : null
              }
              color={
                customerId.value === undefined || customerId.value === ""
                  ? "disabled"
                  : "primary"
              }
              fontSize="medium"
              sx={{ cursor: "pointer" }}
            />
          </Tooltip>
        )}

        {Constants.loggedInUser.usertype === "E" ? (
          <LoadingButton
            component="label"
            variant="outlined"
            size="small"
            color="secondary"
            sx={{ ml: "5px" }}
            loading={uploadLoader}
            loadingPosition="start"
            startIcon={
              <ReplyIcon size={20} style={{ transform: "scaleX(-1)" }} />
            }
            onClick={generateLink}
            disabled={customerId === "" || searchCriteria === ""}
          >
            Share
          </LoadingButton>
        ) : null}

        {Utility.isReadAllowed(mixPermission) ? (
          <Tooltip title="Add To Mix">
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<BsBucketFill size={16} />}
              color="success"
              disabled={selectionModel.length === 0 || disableAllButtons}
              onClick={() => {
                let stoneDataArray = [];
                let bucketData = [];
                selectionModel.map((stoneid) => {
                  let iData = stoneDataMap.get(stoneid);
                  if (iData.status !== "AVAILABLE") {
                    stoneDataArray.push(iData.stoneid);
                  } else {
                    iData.mixprice = null;
                    iData.mixtotal = null;
                    bucketData.push(iData);
                  }
                  return stoneDataArray;
                });
                if (stoneDataArray.length === 0) {
                  setShowMixBucketModal(true);
                  setBucketData(bucketData);
                } else if (stoneDataArray.length > 0) {
                  setWarning(true);
                  setWarningMessage(
                    'To perform this action, Please select stones with status "AVAILABLE"'
                  );
                }
              }}
            >
              MIX
            </Button>
          </Tooltip>
        ) : null}

        {Utility.isReadAllowed(offlinePermission) ? (
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginLeft: "5px",
            }}
          >
            <Box
              sx={{
                "& .MuiButton-startIcon": {
                  marginRight: 0,
                  marginLeft: "-2px",
                },
                "& .MuiLoadingButton-loadingIndicator": { left: "7px" },
              }}
            >
              <LoadingButton
                id="demo-customized-offline-button"
                aria-controls={
                  offlineMenu ? "demo-customized-offline-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={offlineMenu ? "true" : undefined}
                variant="outlined"
                disableElevation
                disabled={selectionModel.length === 0 || disableAllButtons}
                onClick={(e) => setOfflineMenu(e.currentTarget)}
                sx={{
                  color: "#2e7d32",
                  borderRadius: "4px",
                  border: "1px solid rgba(46, 125, 50, 0.5)",
                  padding: "2px 2px 2px 8px",
                  cursor: "pointer",
                }}
                loading={offlineLoader}
                loadingPosition="start"
                startIcon={<MdUnpublished size={18} />}
              >
                <span style={{ fontSize: "14px", marginLeft: "4px" }}>
                  OFFLINE
                </span>
                <KeyboardArrowDownIcon />
              </LoadingButton>

              <StyledMenuButtons
                id="demo-customized-offline-menu"
                MenuListProps={{
                  "aria-labelledby": "demo-customized-offline-button",
                  style: {
                    maxHeight: 250,
                  },
                }}
                anchorEl={offlineMenu}
                open={offlineMenu}
                onClose={() => setOfflineMenu(null)}
              >
                <MenuItem
                  onClick={() => {
                    setOfflineMenu(null);
                    let stoneDataArray = [];
                    selectionModel.map((stoneid) => {
                      let iData = stoneDataMap.get(stoneid);
                      if (iData.status !== "AVAILABLE") {
                        stoneDataArray.push(iData.stoneid);
                      }
                      return stoneDataArray;
                    });
                    if (stoneDataArray.length === 0) {
                      markStoneStatusToOffline();
                    } else if (stoneDataArray.length > 0) {
                      setWarning(true);
                      setWarningMessage(
                        'To perform this action, Please select stones with status "AVAILABLE"'
                      );
                    }
                  }}
                  disableRipple
                >
                  <MdUnpublished size={18} />
                  <span style={{ marginLeft: "5px" }}>OFFLINE</span>
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setOfflineMenu(null);
                    let stoneDataArray = [];
                    selectionModel.map((stoneid) => {
                      let iData = stoneDataMap.get(stoneid);
                      if (iData.status !== "OFFLINE") {
                        stoneDataArray.push(iData.stoneid);
                      }
                      return stoneDataArray;
                    });
                    if (stoneDataArray.length === 0) {
                      markStoneStatusToAvailable();
                    } else if (stoneDataArray.length > 0) {
                      setWarning(true);
                      setWarningMessage(
                        'To perform this action, Please select stones with status "OFFLINE"'
                      );
                    }
                  }}
                  disableRipple
                >
                  <IoMdCheckmarkCircle size={18} />
                  <span style={{ marginLeft: "5px" }}>ONLINE</span>
                </MenuItem>
              </StyledMenuButtons>
            </Box>
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginLeft: "5px",
            "& .MuiButton-startIcon": {
              marginRight: "0px !important",
              marginLeft: "-2px",
            },
            "& .MuiLoadingButton-loadingIndicator": { left: "7px" },
          }}
        >
          <LoadingButton
            id="demo-customized-export-menu"
            aria-controls={
              exportMenu ? "demo-customized-export-menu" : undefined
            }
            aria-haspopup="true"
            aria-expanded={exportMenu ? "true" : undefined}
            variant="outlined"
            disableElevation
            disabled={stockData.length === 0}
            onClick={(e) => setExportMenu(e.currentTarget)}
            sx={{
              color: "#2e7d32",
              borderRadius: "4px",
              border: "1px solid rgba(46, 125, 50, 0.5)",
              padding: "2px 2px 2px 8px",
              cursor: "pointer",
            }}
            loadingPosition="start"
            startIcon={<TbTableExport size={18} />}
          >
            <span style={{ fontSize: "14px", marginLeft: "4px" }}>EXPORT</span>
            <KeyboardArrowDownIcon />
          </LoadingButton>

          <StyledMenuButtons
            id="demo-customized-export-menu"
            MenuListProps={{
              "aria-labelledby": "demo-customized-export-menu",
              style: {
                maxHeight: 250,
              },
            }}
            anchorEl={exportMenu}
            open={exportMenu}
            onClose={() => setExportMenu(null)}
          >
            <MenuItem>
              {Utility.isReadAllowed(fullExcelPermission) &&
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  color="secondary"
                  sx={{
                    ml: "5px",
                    minWidth: buttonMinWidth,
                    justifyContent: "flex-start",
                    textAlign: "left",
                    "& .MuiButton-startIcon": {
                      marginRight: "1px",
                      marginLeft: "0px",
                    },
                  }}
                  startIcon={<Download />}
                  onClick={() => {
                    getExcelData(true);
                    setExportMenu(null);
                  }}
                >
                  FULL EXCEL
                </Button>
              }
            </MenuItem>
            <MenuItem>
              <Button
                component="label"
                variant="outlined"
                size="small"
                color="secondary"
                sx={{
                  ml: "5px",
                  minWidth: buttonMinWidth,
                  justifyContent: "flex-start",
                  textAlign: "left",
                  "& .MuiButton-startIcon": {
                    marginRight: "1px",
                    marginLeft: "0px",
                  },
                }}
                startIcon={<Download />}
                onClick={() => {
                  getExcelData(false);
                  setExportMenu(null);
                }}
              >
                EXCEL
              </Button>
            </MenuItem>

            <MenuItem>
              <Button
                component="label"
                variant="outlined"
                size="small"
                color="secondary"
                sx={{
                  ml: "5px",
                  minWidth: buttonMinWidth,
                  justifyContent: "flex-start",
                  textAlign: "left",
                  "& .MuiButton-startIcon": {
                    marginRight: "1px",
                    marginLeft: "0px",
                  },
                }}
                startIcon={<Download size="large" />}
                onClick={() => {
                  createPdf()
                  setExportMenu(null);
                }}
              >
                PDF
              </Button>
            </MenuItem>
          </StyledMenuButtons>
        </Box>

        {Utility.isReadAllowed(offerPermission) ? (
          <Tooltip title="Offer">
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<LocalOfferIcon size="large" />}
              color="success"
              disabled={selectionModel.length === 0 || disableAllButtons}
              onClick={() => {
                if (!checkStoneOffline()) {
                  setShowOfferModal(true);
                }
              }}
            >
              OFFER
            </Button>
          </Tooltip>
        ) : null}

        {Utility.isReadAllowed(relabPermission) ? (
          <Tooltip title="RE-LAB">
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<BiotechIcon size="large" />}
              color="success"
              disabled={selectionModel.length === 0 || disableAllButtons}
              onClick={() => validateRelabStones()}
            >
              RE-LAB
            </Button>
          </Tooltip>
        ) : null}

        {Utility.isReadAllowed(holdPermission) ? (
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginLeft: "5px",
            }}
          >
            <Box>
              <Button
                id="demo-customized-hold-button"
                aria-controls={
                  holdMenu ? "demo-customized-hold-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={holdMenu ? "true" : undefined}
                variant="outlined"
                disableElevation
                disabled={selectionModel.length === 0 || disableAllButtons}
                onClick={(e) => setHoldMenu(e.currentTarget)}
                sx={{
                  color: "#2e7d32",
                  borderRadius: "4px",
                  border: "1px solid rgba(46, 125, 50, 0.5)",
                  padding: "2px 2px 2px 8px",
                  cursor: "pointer",
                }}
              >
                <FaBoxOpen size={18} />
                <span style={{ fontSize: "14px", marginLeft: "8px" }}>
                  HOLD
                </span>
                <KeyboardArrowDownIcon />
              </Button>

              <StyledMenuButtons
                id="demo-customized-hold-menu"
                MenuListProps={{
                  "aria-labelledby": "demo-customized-hold-button",
                  style: {
                    maxHeight: 250,
                  },
                }}
                anchorEl={holdMenu}
                open={holdMenu}
                onClose={() => setHoldMenu(null)}
              >
                <MenuItem
                  onClick={() => {
                    setHoldMenu(null);
                    let stoneDataArray = [];
                    let data = [];
                    selectionModel.map((stoneid) => {
                      let iData = stoneDataMap.get(stoneid);
                      data.push({
                        id: iData.stoneid,
                        status: iData.status,
                      });
                      if (
                        (iData.status !== "AVAILABLE" &&
                          iData.status !== "MEMO_OUT") ||
                        (iData.status === "MEMO_OUT" &&
                          iData.memocustomerid !== customerId.value)
                      ) {
                        stoneDataArray.push(iData.stoneid);
                      }
                      return stoneDataArray;
                    });
                    if (stoneDataArray.length === 0) {
                      setShowHoldModal(true);
                      setHoldData(data);
                    } else if (stoneDataArray.length > 0) {
                      setWarning(true);
                      setWarningMessage(
                        'To perform this action, Please select stones with status "AVAILABLE" or "MEMO_OUT" stones of your customer'
                      );
                    }
                  }}
                  disableRipple
                >
                  <FaBoxOpen size={18} />
                  <span style={{ marginLeft: "5px" }}>HOLD</span>
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setHoldMenu(null);
                    let stoneDataArray = [];
                    let differentCustomerid;
                    let flag = false;
                    let data = [];
                    selectionModel.map((stoneid) => {
                      let iData = stoneDataMap.get(stoneid);
                      data.push({
                        id: iData.stoneid,
                        status: iData.status,
                      });
                      if (
                        iData.status !== "HOLD" ||
                        (iData.status === "HOLD" &&
                          ((Constants.loggedInUser.usertype === "E" &&
                            iData.holdstoneuserid !==
                            Constants.loggedInUser.id &&
                            !customerOptions.includes(
                              iData.holdstonecustomerid
                            )) ||
                            (Constants.loggedInUser.usertype === "B" &&
                              iData.holdstoneuserid !==
                              Constants.loggedInUser.id) ||
                            (Constants.loggedInUser.usertype === "C" &&
                              iData.holdstoneuserid !==
                              Constants.loggedInUser.id) ||
                            iData.holdstonecustomerid !== customerId.value))
                      ) {
                        stoneDataArray.push(iData.stoneid);
                      } else if (!flag && iData.holdstonecustomerid !== null) {
                        differentCustomerid = iData.holdstonecustomerid;
                        flag = true;
                      } else if (
                        iData.holdstonecustomerid !== null &&
                        differentCustomerid !== iData.holdstonecustomerid
                      ) {
                        stoneDataArray.push(iData.stoneid);
                      }
                      return stoneDataArray;
                    });
                    if (stoneDataArray.length === 0) {
                      setShowReleaseModal(true);
                      setHoldData(data);
                    } else if (stoneDataArray.length > 0) {
                      setWarning(true);
                      setWarningMessage(
                        'To perform this action, Please select stones which you have "HOLD"'
                      );
                    }
                  }}
                  disableRipple
                >
                  <FaBoxOpen size={18} />
                  <span style={{ marginLeft: "5px" }}>RELEASE</span>
                </MenuItem>
              </StyledMenuButtons>
            </Box>
          </Box>
        ) : null}

        {Utility.isReadAllowed(sellPermission) ? (
          <Tooltip title="Sell Stones">
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<DiamondOutlinedIcon size="large" />}
              color="success"
              disabled={selectionModel.length === 0 || disableAllButtons}
              onClick={() => {
                let stoneDataArray = [];
                let differentCustomerid;
                let differentCustomerCompanyName;
                let flag = false;
                selectionModel.map((stoneid) => {
                  let iData = stoneDataMap.get(stoneid);
                  if (
                    iData.status !== "AVAILABLE" &&
                    (iData.status === "OFFLINE" ||
                      (iData.status === "MEMO_OUT" &&
                        iData.memocustomerid !== customerId.value) ||
                      (iData.status === "HOLD" &&
                        ((Constants.loggedInUser.usertype === "C" &&
                          iData.holdstoneuserid !==
                          Constants.loggedInUser.id) ||
                          (Constants.loggedInUser.usertype === "B" &&
                            iData.holdstoneuserid !==
                            Constants.loggedInUser.id) ||
                          (Constants.loggedInUser.usertype === "E" &&
                            iData.holdstoneuserid !==
                            Constants.loggedInUser.id &&
                            !customerOptions.includes(
                              iData.holdstonecustomerid
                            )) ||
                          iData.holdstonecustomerid !== customerId.value)))
                  ) {
                    stoneDataArray.push(iData.stoneid);
                  } else if (!flag && iData.holdstonecustomerid !== null) {
                    differentCustomerid = iData.holdstonecustomerid;
                    differentCustomerCompanyName =
                      iData.stoneholdcustomercompanyname;
                    flag = true;
                  } else if (
                    iData.holdstonecustomerid !== null &&
                    differentCustomerid !== iData.holdstonecustomerid
                  ) {
                    stoneDataArray.push(iData.stoneid);
                  }
                  return stoneDataArray;
                });
                if (stoneDataArray.length === 0) {
                  setShowSellModal(true);
                  if (differentCustomerid) {
                    setCustomerId({
                      label: differentCustomerCompanyName,
                      value: differentCustomerid,
                    });
                  }
                } else if (stoneDataArray.length > 0) {
                  setWarning(true);
                  setWarningMessage(
                    'To perform this action, Please select stones with status "AVAILABLE" OR the stone which you have "HOLD"'
                  );
                }
              }}
            >
              {Utility.GetBuyOrSell()}
            </Button>
          </Tooltip>
        ) : null}

        {Utility.isAddAllowed(local_Permission) && activeTabValue === "Natural" ? (
          <Tooltip title="Create Memo">
            <Button
              color="primary"
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<AddBoxOutlinedIcon size="large" />}
              disabled={selectionModel.length === 0 || disableAllButtons}
              onClick={() => {
                if (!checkStoneOffline()) {
                  handleOpen();
                }
              }}
            >
              Memo
            </Button>
          </Tooltip>
        ) : null}

        <Tooltip title="Add to Watchlist">
          <LoadingButton
            color="primary"
            variant="outlined"
            size="small"
            component="label"
            sx={{ ml: "5px" }}
            disabled={selectionModel.length === 0 || disableAllButtons}
            onClick={() => {
              if (!checkStoneOffline()) {
                if (selectionModel.length > 0) {
                  let dataArray = rows.filter((iRow) =>
                    selectionModel.includes(iRow.stoneid)
                  );
                  setWatchlistData(dataArray);
                  setShowWatchlistModal(true);
                }
              }
            }}
            loading={watchlistLoader}
            loadingPosition="start"
            startIcon={<VisibilityOutlined size="large" />}
          >
            Watchlist
          </LoadingButton>
        </Tooltip>

        <GridToolbarQuickFilter debounceMs={500} />
      </GridToolbarContainer>
    );
  }

  return !allowed ? (
    <NoPermission />
  ) : (
    <div>
      {/* Modals */}
      <div>
        <Drawer
          anchor="left"
          open={drawerExpanded}
          style={{ width: "50%" }}
          onClose={() => setDrawerExpanded(!drawerExpanded)}
        >
          <StockSearchCriteria
            displaySearchCriteria={true}
            stockCriteriaStored={stockCriteriaState}
            handleSearch={handleSearch}
            showSaveSearchAs={true}
            setCustomerId={setCustomerId}
            setCustomerOptions={setCustomerOptions}
            drawerClose={() => setDrawerExpanded(false)}
            setFilters={setFilters}
            readOnly={false}
            setSelectionModel={setSelectionModel}
            setFlag={setFlag}
            guid={flag ? guid : undefined}
            enableReportNoSearchBox={true}
          />
        </Drawer>

        {showStoneDetailModal ? (
          <StoneDetailModal
            show={showStoneDetailModal}
            stoneId={stoneId}
            handleClose={handleClose}
          />
        ) : null}

        {showKapanDetailModal ? (
          <KapanHistoryModal
            show={showKapanDetailModal}
            kapanId={kapanId}
            close={() => setShowKapanDetailModal(false)}
          />
        ) : null}

        {showStoneMediaModal ? (
          <StoneMediaModal
            show={showStoneMediaModal}
            stoneMediaURL={stoneMediaURL}
            handleClose={() => setShowStoneMediaModal(false)}
          />
        ) : null}

        {showWatchlistModal ? (
          <WatchlistModal
            stoneData={watchlistData}
            handleSave={addToWatchlist}
            watchlistLoader={watchlistLoader}
            handleClose={() => setShowWatchlistModal(false)}
          />
        ) : null}

        <CommonSnackbar
          show={errorSnackbar}
          message={Constants.APiFailErrorMessage}
          variant={"error"}
          autoHide={Constants.SnackBarCloseTimer}
          title={"Failed"}
          handleClose={() => setErrorSnackbar(false)}
        />

        {warning ? (
          <CommonSnackbar
            show={true}
            message={warningmessage}
            variant={"warning"}
            autoHide={false}
            title={"Warning"}
            handleClose={handleClose}
          />
        ) : null}

        {showSuccessSnackbar ? (
          <CommonSnackbar
            show={true}
            message={successMessage}
            variant={snackbarVariant}
            autoHide={Constants.SnackBarCloseTimer}
            title={snackbarVariant === "success" ? "Success" : "Failed"}
            handleClose={handleClose}
          />
        ) : null}

        {showPacketDetailModal ? (
          <PacketHistoryModal
            show={showPacketDetailModal}
            packetId={packetId}
            kapanId={kapanId}
            close={handleClose}
          />
        ) : null}

        {messageModal ? (
          <SendMessageModal
            show={messageModal}
            textmessage={message}
            handleSendMessage={handleSendMessage}
            handleClose={() => setMessageModal(false)}
          />
        ) : null}

        {showCriteriaLinkModal ? (
          <CriteriaLinkModal
            guid={generatedLink}
            handleClose={() => setShowCriteriaLinkModal(false)}
            show={true}
          />
        ) : null}

        {showMixBucketModal && (
          <MixStoneBucketModal
            show={showMixBucketModal}
            setShow={setShowMixBucketModal}
            handleSave={handleMixBucketStoneSave}
            sourceData={bucketData}
            loading={loadingButton}
          />
        )}

        {showOfferModal ? (
          <OfferModal
            show={true}
            setShow={setShowOfferModal}
            handleValidation={handleValidation}
            handleOffer={createOffer}
            stoneId={selectionModel}
            customer={customerId}
            user={
              Constants.loggedInUser.id +
              " | " +
              Constants.loggedInUser.companyname
            }
            loadingOfferButton={loadingButton}
            notPermitted={false}
          />
        ) : null}

        {showHoldModal ? (
          <HoldMemoModal
            show={true}
            setShow={setShowHoldModal}
            handleValidation={handleValidation}
            handleSave={holdStones}
            data={holdData}
            customer={customerId}
            loading={loadingButton}
          />
        ) : null}

        {showReleaseModal && (
          <ReleaseStoneModal
            show={true}
            setShow={setShowReleaseModal}
            handleValidation={handleValidation}
            handleSave={releaseStone}
            data={holdData}
            stoneId={selectionModel}
            customer={customerId.value}
            loading={loadingButton}
          />
        )}

        {showRelabModal ? (
          <RelabModal
            show={true}
            setShow={setShowRelabModal}
            handleSave={relabStones}
            stoneIds={selectionModel}
            loading={loadingButton}
          />
        ) : null}

        {showSellModal ? (
          <SellMemoModal
            show={true}
            setShow={setShowSellModal}
            handleValidation={handleValidation}
            handleInvoice={sellStonesInvoice}
            handleProforma={createProforma}
            stoneId={selectionModel}
            customer={customerId}
            user={
              Constants.loggedInUser.id +
              " | " +
              Constants.loggedInUser.companyname
            }
            proformaButton={true}
            loading={loadingButton}
            loadingProformaButton={loadingProformaButton}
          />
        ) : null}

        {show ? (
          <CreateMemoModal
            show={true}
            setShow={setShow}
            handleValidation={handleValidation}
            handleSave={createMemo}
            stoneId={selectionModel}
            customer={customerId}
            loading={loadingButton}
          />
        ) : null}
      </div>

      {/* Summary Container */}
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "8px 0px",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <Tooltip title="Show All Filters">
            <Button
              variant="contained"
              size="small"
              color="inherit"
              sx={{
                backgroundColor: "#454545",
                color: "white",
                height: "42px",
                "&:hover": { backgroundColor: "#454545" },
              }}
              startIcon={<TuneIcon size="large" />}
              onClick={() => setDrawerExpanded(true)}
            >
              All Filters
            </Button>
          </Tooltip>

          <Box style={{ fontWeight: Constants.defaultFontWeight }}>
            {" "}
            {customerId !== "" && customerId.label}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
            flexBasis: 0,
            overflow: "hidden",
            color: "black",
            fontWeight: "normal",
            px: 1,
            textAlign: "center",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            flexWrap="wrap"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              justifyContent: "center",
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              No of Stones: <b>{noOfSelectedStones}</b>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              Total Weight: <b>{selectedTotalSize}</b>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              Total Price: <b>{selectedtotalPrice}</b>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              Avg Price: <b>{selectedavgPrice}</b>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              Avg Discount: <b>{selectedAvgDiscount}</b>
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "13rem",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              "& .MuiFormControlLabel-root": {
                margin: 0,
              },
              "& .MuiSwitch-root": {
                width: "38px",
                height: "22px",
              },
              "& .MuiSwitch-thumb": {
                width: "18px !important",
                height: "18px !important",
              },
            }}
          >
            <FormControlLabel
              control={<NewStoneSwitch sx={{ m: 1 }} />}
              disabled={allStoneList.length <= 0}
              onChange={() => setShowNewStone(!showNewStone)}
              checked={showNewStone && allStoneList.length > 0}
            />
            <Typography
              sx={{
                fontSize: "15px",
                color: allStoneList.length > 0 ? "black" : "#E0E0E0",
              }}
            >
              New Arrival
            </Typography>
          </Box>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div>
              <Box
                id="demo-customized-button"
                aria-controls={open ? "demo-customized-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                variant="outlined"
                disableElevation
                onClick={handleClick}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  color: "#454545",
                  borderColor: "#454545",
                  borderRadius: "4px",
                  border: "1px solid black",
                  padding: "5px 0 5px 5px",
                  cursor: "pointer",
                }}
              >
                <img
                  src={colourchart}
                  alt="Color Scheme"
                  style={{
                    height: "24px",
                    width: "24px",
                  }}
                />
                <KeyboardArrowDownIcon />
              </Box>
              <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                  "aria-labelledby": "demo-customized-button",
                  style: {
                    maxHeight: 290,
                  },
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseButton}
              >
                <MenuItem onClick={handleCloseButton} disableRipple>
                  <span
                    style={{
                      height: "15px",
                      width: "15px",
                      backgroundColor: "#000",
                      borderRadius: 20,
                      margin: 10,
                    }}
                  ></span>
                  AVAILABLE
                </MenuItem>
                <MenuItem onClick={handleCloseButton} disableRipple>
                  <span
                    style={{
                      height: "15px",
                      width: "15px",
                      backgroundColor: "#B6D7A8",
                      borderRadius: 20,
                      margin: 10,
                    }}
                  ></span>
                  <b>HOLD</b> / MEMO_OUT (Selected Customer)
                </MenuItem>
                <MenuItem onClick={handleCloseButton} disableRipple>
                  <span
                    style={{
                      height: "15px",
                      width: "15px",
                      backgroundColor: "#F1E68C",
                      borderRadius: 20,
                      margin: 10,
                    }}
                  ></span>
                  <b>HOLD</b> / MEMO_OUT (My Customers)
                </MenuItem>
                <MenuItem onClick={handleCloseButton} disableRipple>
                  <span
                    style={{
                      height: "15px",
                      width: "15px",
                      backgroundColor: "#b6b9d6",
                      borderRadius: 20,
                      margin: 10,
                    }}
                  ></span>
                  <b>HOLD</b> / MEMO_OUT (Others)
                </MenuItem>
                <MenuItem onClick={handleCloseButton} disableRipple>
                  <span
                    style={{
                      height: "15px",
                      width: "15px",
                      backgroundColor: "#FEBD7D",
                      borderRadius: 20,
                      margin: 10,
                    }}
                  ></span>
                  AVAILABLE (IN_TRANSIT)
                </MenuItem>
              </StyledMenu>
            </div>
          </div>
        </Box>
      </Box>

      {/* Data Grid */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          width: "100%",
          position: "relative",
          "& .MuiTablePagination-selectLabel": {
            marginTop: "15px",
          },
          "& .MuiTablePagination-displayedRows": {
            marginTop: "15px",
          },
          "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .css-cc8tf1": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .greenCell": {
            color: "#080808",
            backgroundColor: "#B6D7A8",
          },
          "& .yellowCell": {
            color: "#080808",
            backgroundColor: "#F1E68C",
          },
          height: "83vh",
        }}
      >
        <StyledDataGrid
          disableRowSelectionOnClick={true}
          localeText={{ noRowsLabel: "No Record Found" }}
          sx={{
            height: "100%",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
          }}
          slots={{
            toolbar: CustomToolbar,
          }}
          rows={rows}
          // style={{ height: "25000pt" }}
          sortModel={sortModel}
          onSortModelChange={(newModel) => setSortModel(newModel)}
          density="compact"
          columns={columns}
          getRowId={(row) => row.stoneid}
          checkboxSelection={true}
          onRowSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          loading={stockLoader}
          rowSelectionModel={selectionModel}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns id, the other columns will remain visible
                locationid: false,
                kapanid: false,
                country: false,
                quality: false,
                shade: false,
                repacode: false,
                createdat: false,
                height: false,
                width: false,
                packetid: false,
                crownheight: false,
                culetsize: false,
                culetcondition: false,
                gridlemin: false,
                intensitylongtitle: false,
                laserinscription: false,
                note: false,
                paviliondepth: false,
                gridlecondition: false,
                gridlepercent: false,
                pavilionangle: false,
                white: false,
                stonetype: false,
              },
            },
          }}
          pageSizeOptions={Constants.pageSizeOptions}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
        />
      </Box>
    </div>
  );
}